import { useEffect, useMemo, useState } from 'preact/hooks';
import { usePpPortalAuthenticated } from '../../lib/pp-user-auth.js';
import { groupLandingPackages } from './landing-packages.js';

function formatInt(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return Math.round(num).toLocaleString('tr-TR');
}

function formatMoney(value, digits = 0) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function checkoutHref(appBase, packageId) {
  return `${appBase}/accounts/billing/checkout/?package_id=${encodeURIComponent(packageId || '')}`;
}

function packageCtaHref(appBase, packageId, isAuthenticated) {
  const checkout = checkoutHref(appBase, packageId);
  if (isAuthenticated) return checkout;
  return `${appBase}/accounts/login/?next=${encodeURIComponent(checkout)}`;
}

function FeatureRow({ icon, children, highlight = false }) {
  return (
    <div class={`pp-landing-pkg__feature${highlight ? ' is-highlight' : ''}`}>
      <i class={`fas ${icon}`} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function PackageFeatures({ pkg, kind, seatGiftCredits }) {
  const credits = Number(pkg.credits) || 0;
  const monthlyCredits = Number(pkg.monthly_credits) || 0;
  const maxUsers = Number(pkg.max_users) || 0;
  const discount = Number(pkg.discount_percent) || 0;
  const droneCount = Number(pkg.bundled_drone_video_count) || 0;
  const aiCount = Number(pkg.bundled_ai_video_count) || 0;
  const droneSave = Number(pkg.bundled_drone_savings_try) || 0;
  const aiSave = Number(pkg.bundled_ai_savings_try) || 0;
  const extraFeatures = Array.isArray(pkg.features) ? pkg.features.filter(Boolean) : [];

  return (
    <div class="pp-landing-pkg__features">
      <FeatureRow icon="fa-check">ProSorgu 1 Kredi</FeatureRow>
      <FeatureRow icon="fa-check">Emlak İlanı Yayınlamak 1 Kredi</FeatureRow>
      {kind === 'yearly' ? (
        <FeatureRow icon="fa-check">
          {formatInt(credits)} Tepe Kredi ({formatInt(monthlyCredits)}/ay)
        </FeatureRow>
      ) : (
        <FeatureRow icon="fa-check">{formatInt(credits)} Tepe Kredi</FeatureRow>
      )}
      {droneCount > 0 ? (
        <FeatureRow icon="fa-helicopter">
          {formatInt(droneCount)} Sanal Drone Video
          {droneSave > 0 ? ` — ${formatInt(droneSave)} TL tasarruf` : ''}
        </FeatureRow>
      ) : null}
      {aiCount > 0 ? (
        <FeatureRow icon="fa-video">
          {formatInt(aiCount)} AI Video
          {aiSave > 0 ? ` — ${formatInt(aiSave)} TL tasarruf` : ''}
        </FeatureRow>
      ) : null}
      {kind !== 'ek' && maxUsers > 0 ? (
        <FeatureRow icon="fa-users">{formatInt(maxUsers)} kullanıcıya kadar</FeatureRow>
      ) : null}
      {kind === 'monthly' ? <FeatureRow icon="fa-check">1 Aylık Geçerlilik</FeatureRow> : null}
      {kind === 'yearly' ? <FeatureRow icon="fa-check">12 Aylık Geçerlilik</FeatureRow> : null}
      {kind === 'yearly' && discount > 0 ? (
        <FeatureRow icon="fa-tag" highlight>
          %{formatInt(discount)} Tasarruf
        </FeatureRow>
      ) : null}
      {kind !== 'ek' ? <FeatureRow icon="fa-check">Tüm Özelliklere Erişim</FeatureRow> : null}
      {kind !== 'ek' && maxUsers > 0 && seatGiftCredits > 0 ? (
        <FeatureRow icon="fa-gift">Kullanıcı + {formatInt(seatGiftCredits)} coin hediye</FeatureRow>
      ) : null}
      {kind === 'ek'
        ? extraFeatures.map((feat) => (
            <FeatureRow icon="fa-check" key={feat}>
              {feat}
            </FeatureRow>
          ))
        : null}
    </div>
  );
}

function LandingPackageCard({ pkg, kind, appBase, isAuthenticated, seatGiftCredits }) {
  const discount = Number(pkg.discount_percent) || 0;
  const credits = Number(pkg.credits) || 0;
  const monthlyCredits = Number(pkg.monthly_credits) || 0;
  const originalMonthly = Number(pkg.original_monthly_price) || Number(pkg.original_price) || 0;
  const monthlyPrice = Number(pkg.monthly_price) || 0;
  const originalTotal = Number(pkg.original_price) || 0;
  const totalPrice = Number(pkg.discounted_price ?? pkg.price) || 0;
  const unitPrice = Number(pkg.price_per_credit) || 0;
  const name = pkg.pricing_display_name || pkg.name || 'Paket';
  const href = packageCtaHref(appBase, pkg.id, isAuthenticated);

  return (
    <article
      class={`pp-landing-pkg__card${pkg.is_popular ? ' is-popular' : ''}`}
      data-package-id={pkg.id}
      data-package-slug={pkg.slug || ''}
    >
      {pkg.is_popular ? <div class="pp-landing-pkg__badge">En Popüler</div> : null}
      <h3 class="pp-landing-pkg__name">{name}</h3>
      <div class="pp-landing-pkg__credits">
        <span class="pp-landing-pkg__credits-value">
          {formatInt(kind === 'yearly' ? monthlyCredits || credits : credits)}
        </span>
        {kind === 'yearly' ? (
          <span class="pp-landing-pkg__credits-label">Toplam {formatInt(credits)} Tepe Kredi</span>
        ) : (
          <span class="pp-landing-pkg__credits-label">Tepe Kredi</span>
        )}
      </div>
      <div class="pp-landing-pkg__price">
        {discount > 0 ? (
          <div class="pp-landing-pkg__original">
            <span class="pp-landing-pkg__strike">
              {formatInt(kind === 'ek' ? originalTotal : originalMonthly)} TL
            </span>
            <span class="pp-landing-pkg__discount">%{formatInt(discount)}</span>
          </div>
        ) : null}
        <div class="pp-landing-pkg__current">
          <span class="pp-landing-pkg__amount">
            {formatInt(kind === 'ek' ? totalPrice : monthlyPrice)}
          </span>
          <span class="pp-landing-pkg__currency">TL</span>
          {kind !== 'ek' ? <span class="pp-landing-pkg__period">/ay</span> : null}
        </div>
        {kind === 'yearly' ? (
          <div class="pp-landing-pkg__total">
            Yıllık toplam: <strong>{formatInt(totalPrice)}</strong> TL
          </div>
        ) : null}
        {credits > 0 && unitPrice > 0 ? (
          <div class="pp-landing-pkg__unit">
            1 Kredi = <strong>{formatMoney(unitPrice, 2)}</strong> TL
          </div>
        ) : null}
      </div>
      <PackageFeatures pkg={pkg} kind={kind} seatGiftCredits={seatGiftCredits} />
      <a class="pp-landing-pkg__cta" href={href}>
        {isAuthenticated ? (
          <>
            <i class="fas fa-shopping-cart" aria-hidden="true" /> Satın Al
          </>
        ) : (
          <>
            <i class="fas fa-sign-in-alt" aria-hidden="true" /> Giriş Yap
          </>
        )}
      </a>
    </article>
  );
}

function PackageColumn({ title, packages, kind, emptyText, appBase, isAuthenticated, seatGiftCredits }) {
  return (
    <section class={`pp-landing-pkg__column${kind === 'ek' ? ' pp-landing-pkg__column--tek' : ''}`}>
      <h3 class="pp-landing-pkg__column-title">{title}</h3>
      <div class="pp-landing-pkg__stack">
        {packages.length === 0 ? (
          <p class="pp-landing-pkg__empty">{emptyText}</p>
        ) : (
          packages.map((pkg) => (
            <LandingPackageCard
              key={pkg.id || pkg.slug}
              pkg={pkg}
              kind={kind}
              appBase={appBase}
              isAuthenticated={isAuthenticated}
              seatGiftCredits={seatGiftCredits}
            />
          ))
        )}
      </div>
    </section>
  );
}

export function LandingCreditPackages({ appBase }) {
  const isAuthenticated = usePpPortalAuthenticated();
  const [packages, setPackages] = useState([]);
  const [seatGiftCredits, setSeatGiftCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const groups = useMemo(() => groupLandingPackages(packages), [packages]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/packages/?platform=web', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('packages'))))
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.data?.packages) ? data.data.packages : [];
        setPackages(rows);
        setSeatGiftCredits(Number(data?.data?.seat_gift_credits) || 0);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section class="pp-landing-pkg" id="landing-packages" aria-labelledby="landing-packages-title">
      <div class="pp-landing-inner pp-landing-pkg__inner">
        <header class="pp-landing-pkg__head">
          <span class="pp-landing-pkg__eyebrow">
            <i class="fas fa-coins" aria-hidden="true" />
            Tepe Kredi
          </span>
          <h2 id="landing-packages-title" class="pp-landing-pkg__title">
            Kredi Paketleri
          </h2>
          <p class="pp-landing-pkg__lead">
            İhtiyacınıza uygun paketi seçin — fiyatlar ve paketler pricing sayfasıyla aynı katalogdan yüklenir.
          </p>
        </header>

        {loading ? (
          <p class="pp-landing-pkg__status" role="status">
            <i class="fas fa-spinner fa-spin" aria-hidden="true" /> Yükleniyor…
          </p>
        ) : null}

        {!loading && error ? (
          <p class="pp-landing-pkg__status pp-landing-pkg__status--error">
            Paketler yüklenemedi.{' '}
            <a href={`${appBase}/accounts/pricing/`}>Paket sayfasına git</a>
          </p>
        ) : null}

        {!loading && !error ? (
          <div class="pp-landing-pkg__columns">
            <PackageColumn
              title="Aylık Paketler"
              packages={groups.monthly}
              kind="monthly"
              emptyText="Henüz paket tanımlanmamış"
              appBase={appBase}
              isAuthenticated={isAuthenticated}
              seatGiftCredits={seatGiftCredits}
            />
            <PackageColumn
              title="Yıllık Paketler"
              packages={groups.yearly}
              kind="yearly"
              emptyText="Henüz yıllık paket tanımlanmamış"
              appBase={appBase}
              isAuthenticated={isAuthenticated}
              seatGiftCredits={seatGiftCredits}
            />
            <PackageColumn
              title="Tek Kredi"
              packages={groups.ek}
              kind="ek"
              emptyText="Henüz tek kredi paketi tanımlanmamış"
              appBase={appBase}
              isAuthenticated={isAuthenticated}
              seatGiftCredits={seatGiftCredits}
            />
          </div>
        ) : null}

        <div class="pp-landing-pkg__footer">
          <a class="pp-landing-chip pp-landing-chip--dark" href={`${appBase}/accounts/pricing/`}>
            Tüm paket detayları
          </a>
          <div class="pp-landing-pkg__footer-nav" role="group" aria-label="Sayfa gezinme">
            <button type="button" class="pp-landing-scroll-hint" data-landing-scroll="up">
              <span class="pp-landing-scroll-hint__label">Tanıtımlar</span>
              <i class="fas fa-chevron-up" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="pp-landing-scroll-hint"
              data-landing-scroll="down"
              aria-label="Yapabilecekleriniz bölümüne git"
            >
              <span class="pp-landing-scroll-hint__label">Yapabilecekleriniz</span>
              <i class="fas fa-chevron-down" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
